import { useMemo } from 'react';
import {
  SwireAppEnum,
  SWIRE_DOMAINS,
  SWIRE_LANGUAGE_ENUM,
  SWIRE_APP_EN_PRIVACY_POLICY_URL,
  SWIRE_APP_EN_TAIKOO_PLACE_TERMS_AND_CONDITIONS_URL,
  SWIRE_APP_EN_PACIFIC_PLACE_OFFICE_TERMS_AND_CONDITIONS_URL,
  SWIRE_APP_ZH_HK_TAIKOO_PLACE_TERMS_AND_CONDITIONS_URL,
  SWIRE_APP_ZH_HK_PACIFIC_PLACE_OFFICE_TERMS_AND_CONDITIONS_URL,
  SWIRE_APP_ZH_HK_PRIVACY_POLICY_URL,
} from 'constants-whitelabel';
import { useTranslation } from 'react-i18next';
import { useFlag } from 'lane-shared/hooks';
import { FeatureFlag } from 'constants-flags';

export function useSwireApp(
  whitelabel: { url?: string },
  location: { hostname: string } = window.location
) {
  const { i18n } = useTranslation();
  const isSwireSignupFlow = useFlag(FeatureFlag.EnableSwireSignupFlow, false); 
  const useSwirePacificPlaceOfficeAppDefault = useFlag(FeatureFlag.UseSwirePacificPlaceOfficeAppDefault, false);
  const currentLanguage = i18n.language;
  const locationHostname = location.hostname;

  return useMemo(() => {
    const url = whitelabel?.url?.trim();

    // Early return if no URL provided
    if (!url || !isSwireSignupFlow) {
      return {
        appType: null,
        isSwire: false,
      };
    }

    // Extract hostname from URL securely
    let urlHostname: string | null = null;

    try {
      const urlObj = new URL(url);

      urlHostname = urlObj.hostname;
    } catch {
      // Invalid URL format
      urlHostname = null;
    }

    // Determine if it's a Swire app using exact hostname matching
    const isSwire =
      urlHostname !== null &&
      SWIRE_DOMAINS.includes(urlHostname) &&
      locationHostname === urlHostname;

    // Determine app type
    let appType: SwireAppEnum | null = useSwirePacificPlaceOfficeAppDefault ? SwireAppEnum.PACIFIC_PLACE_OFFICE : SwireAppEnum.TAIKOO_PLACE; // this is for testing on dev and staging
    let privacyPolicyUrl: string | null = null;
    let termsAndConditionsUrl: string | null = null;

    if (isSwire && urlHostname) {
      if (urlHostname === SwireAppEnum.TAIKOO_PLACE) {
        appType = SwireAppEnum.TAIKOO_PLACE;
      } else if (urlHostname === SwireAppEnum.PACIFIC_PLACE_OFFICE) {
        appType = SwireAppEnum.PACIFIC_PLACE_OFFICE;
      }

      if (currentLanguage === SWIRE_LANGUAGE_ENUM.EN_US) {
        privacyPolicyUrl = SWIRE_APP_EN_PRIVACY_POLICY_URL;
        termsAndConditionsUrl = appType === SwireAppEnum.TAIKOO_PLACE ? SWIRE_APP_EN_TAIKOO_PLACE_TERMS_AND_CONDITIONS_URL : SWIRE_APP_EN_PACIFIC_PLACE_OFFICE_TERMS_AND_CONDITIONS_URL;
      } else if (currentLanguage === SWIRE_LANGUAGE_ENUM.ZH_HK) {
        privacyPolicyUrl = SWIRE_APP_ZH_HK_PRIVACY_POLICY_URL;
        termsAndConditionsUrl = appType === SwireAppEnum.TAIKOO_PLACE ? SWIRE_APP_ZH_HK_TAIKOO_PLACE_TERMS_AND_CONDITIONS_URL : SWIRE_APP_ZH_HK_PACIFIC_PLACE_OFFICE_TERMS_AND_CONDITIONS_URL;
      }
    }

    return {
      appType,
      isSwire,
      privacyPolicyUrl,
      termsAndConditionsUrl,
    };
  }, [whitelabel?.url, currentLanguage, locationHostname, isSwireSignupFlow, useSwirePacificPlaceOfficeAppDefault]);
}
